import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

import { KycRecord } from 'src/api/adminKyc';

import {
  DocPlaceholder,
  FieldLabel,
  ImageSlot,
  ImageSlotImg,
  ImageSlotStatus,
  ImagesGrid,
  SliderCard,
  SliderClose,
  SliderFrame,
  SliderHeader,
  SliderImage,
  SliderNav,
  SliderOverlay,
  SliderShell,
  SliderTitle,
} from './styled';

export const KycImage: React.FC<{ src: string; alt: string; tall?: boolean; onOpen: () => void }> = ({
  src,
  alt,
  tall,
  onOpen,
}) => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setReady(false);
    setFailed(false);
  }, [src]);

  if (failed) {
    return <DocPlaceholder>Could not load image</DocPlaceholder>;
  }

  return (
    <ImageSlot $tall={tall}>
      {!ready && <ImageSlotStatus>Loading…</ImageSlotStatus>}
      <ImageSlotImg
        src={src}
        alt={alt}
        $ready={ready}
        decoding="async"
        loading="eager"
        onLoad={() => setReady(true)}
        onError={() => setFailed(true)}
        onClick={onOpen}
      />
    </ImageSlot>
  );
};

export const KycImageSlider: React.FC<{
  images: string[];
  index: number;
  // eslint-disable-next-line no-unused-vars
  onIndexChange: (nextIndex: number) => void;
  onClose: () => void;
}> = ({ images, index, onIndexChange, onClose }) => {
  const current = images[index];
  const hasMany = images.length > 1;

  const go = useCallback(
    (delta: number) => {
      if (!hasMany) return;
      const next = (index + delta + images.length) % images.length;
      onIndexChange(next);
    },
    [hasMany, index, images.length, onIndexChange],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [go, onClose]);

  return createPortal(
    <SliderOverlay onClick={onClose} role="presentation">
      <SliderShell onClick={(e) => e.stopPropagation()}>
        {hasMany && (
          <SliderNav type="button" $side="left" aria-label="Previous image" onClick={() => go(-1)}>
            <IoChevronBack size={22} color="#111" />
          </SliderNav>
        )}
        <SliderCard>
          <SliderHeader>
            <SliderTitle>Pic {index + 1}</SliderTitle>
            <SliderClose type="button" aria-label="Close image" onClick={onClose}>
              ×
            </SliderClose>
          </SliderHeader>
          <SliderFrame>{current ? <SliderImage src={current} alt={`Pic ${index + 1}`} /> : null}</SliderFrame>
        </SliderCard>
        {hasMany && (
          <SliderNav type="button" $side="right" aria-label="Next image" onClick={() => go(1)}>
            <IoChevronForward size={22} color="#111" />
          </SliderNav>
        )}
      </SliderShell>
    </SliderOverlay>,
    document.body,
  );
};

/**
 * Uploads from the legacy MANUAL flow. Veriff records never reach here — Veriff
 * retains that media and the API stores no URLs for it.
 */
const LegacyDocuments: React.FC<{ record: KycRecord }> = ({ record }) => {
  const [sliderIndex, setSliderIndex] = useState<number | null>(null);

  const front = record.identification_url?.front;
  const back = record.identification_url?.back;
  const photo = record.photo_url;

  const sliderImages = useMemo(
    () => [front, back, photo].filter(Boolean) as string[],
    [front, back, photo],
  );

  useEffect(() => {
    sliderImages.forEach((src) => {
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = src;
    });
  }, [sliderImages]);

  const openSlider = (src: string) => {
    const idx = sliderImages.indexOf(src);
    setSliderIndex(idx >= 0 ? idx : 0);
  };

  return (
    <>
      <ImagesGrid>
        <div>
          <FieldLabel>Document front</FieldLabel>
          {front ? (
            <KycImage src={front} alt="Document front" onOpen={() => openSlider(front)} />
          ) : (
            <DocPlaceholder>No image</DocPlaceholder>
          )}
        </div>
        <div>
          <FieldLabel>Document back</FieldLabel>
          {back ? (
            <KycImage src={back} alt="Document back" onOpen={() => openSlider(back)} />
          ) : (
            <DocPlaceholder>No image</DocPlaceholder>
          )}
        </div>
      </ImagesGrid>

      <div style={{ marginTop: '1rem' }}>
        <FieldLabel>User photo</FieldLabel>
        {photo ? (
          <KycImage src={photo} alt="User photo" tall onOpen={() => openSlider(photo)} />
        ) : (
          <DocPlaceholder>No photo</DocPlaceholder>
        )}
      </div>

      {sliderIndex != null && sliderImages.length > 0 && (
        <KycImageSlider
          images={sliderImages}
          index={sliderIndex}
          onIndexChange={setSliderIndex}
          onClose={() => setSliderIndex(null)}
        />
      )}
    </>
  );
};

export default LegacyDocuments;
